/* ----------------------------------------------------------------------------
 * Data sources — ONE mock/real gate per surface.
 *
 * Every screen reads through a typed fetcher here; nothing else in the app
 * decides where data comes from. Each fetcher is:
 *
 *     USE_MOCK_DATA ? mock (src/data/mock.ts) : real fetch via the api client
 *
 * Real mode targets the deployed contracts (api/openapi.yaml, api/crm.yaml,
 * api/seo.yaml — served by bookedcentral-api/platform/Router.php). Responses
 * are adapted to the EXISTING UI types; where a UI field has no API source yet
 * the adapter returns null/omits it and the screen renders an honest
 * "no data yet" state — real mode never invents numbers.
 *
 * Auth: the api client sends the session cookie (credentials:'include') and
 * echoes the CSRF token on mutations. 401 → redirect to /login (api client);
 * 403 → surfaced as ApiError and rendered as human guidance (DataState).
 * -------------------------------------------------------------------------- */

import { api, ApiError, USE_MOCK_DATA } from '@/lib/api';
import {
  MOCK_ATTENTION,
  MOCK_CONTACTS,
  MOCK_EXEC_SUMMARY,
  MOCK_HEALTH_EVIDENCE,
  MOCK_LEADS,
  MOCK_LEAD_EVENTS,
  MOCK_OPPORTUNITIES,
  MOCK_SEO_GSC_INSIGHTS,
  MOCK_SEO_INTELLIGENCE,
  MOCK_SEO_PROJECTS,
  MOCK_SEO_SEARCH_ANALYTICS,
  MOCK_VITALS,
} from '@/data/mock';
import { formatCurrency } from '@/lib/utils';
import { fetchSeoExecutiveSignal } from './sources-seod';
import { computeBusinessHealth, composeExecSummary } from './executive-composite';
import type { ExecSignals } from './executive-composite';
import type { SeoExecSnapshot } from '@/types/seod';
import type {
  AttentionItem,
  BenchmarkResult,
  Contact,
  DomainOverview,
  Evidence,
  Lead,
  LeadEvent,
  LeadStatus,
  OrganicKeyword,
  Opportunity,
  PipelineStage,
  RankTrackingRow,
  SeoProject,
  VitalSign,
} from '@/types';

/* ============================================================================
 * API DTOs — the real response shapes (contracts + controller envelopes).
 * ========================================================================== */

/** api/openapi.yaml LeadStatus (differs from the UI enum — mapped below). */
type ApiLeadStatus =
  | 'DISCOVERED'
  | 'QUALIFIED'
  | 'CONTACTED'
  | 'REPLIED_INTERESTED'
  | 'REPLIED_NOT_INTERESTED'
  | 'FOLLOWUP_DUE'
  | 'DEMO_BOOKED'
  | 'SIGNED_UP'
  | 'CONVERTED_TENANT'
  | 'BOUNCED_INVALID'
  | 'OPTED_OUT'
  | 'SUPPRESSED'
  | 'ERASED';

/** ScoreSet (openapi.yaml) — evidence is a free-form per-field object. */
interface ScoreSetDto {
  scoring_version: string;
  fit_score: number | null;
  priority_score: number | null;
  migration_difficulty: string | null;
  booking_software: string | null;
  website_quality_score: number | null;
  booking_experience_score: number | null;
  social_activity: Record<string, unknown>;
  evidence: Record<string, unknown>;
  is_manual_override: boolean;
}

/** Lead in API shape (LeadService::toApiShape + optional scores sidecar). */
interface LeadDto {
  lead_ref: string;
  status: ApiLeadStatus | string;
  owner: string | null;
  campaign: string | null;
  source_directory: string | null;
  source_url: string | null;
  business_name: string | null;
  practitioner_name: string | null;
  modality: string | null;
  city_region: string | null;
  country: string | null;
  business_email: string | null;
  website: string | null;
  next_action: string | null;
  next_action_date: string | null;
  last_activity_at: string | null;
  created_at: string;
  is_test_data: boolean;
  scores?: ScoreSetDto | null;
}

/** GET /v1/summary (StatusController::summary). */
interface SummaryDto {
  funnel: Record<string, number>;
  by_campaign: Record<string, number>;
  recent_leads: LeadDto[];
}

/** GET /v1/sending-status (SendingStatusService::snapshot). */
interface SendingStatusDto {
  paused: boolean;
  pause_reason: string | null;
  complaint_rate: number;
  bounce_rate: number;
  thresholds?: {
    complaint_warn?: number;
    complaint_stop?: number;
    bounce_warn?: number;
    bounce_stop?: number;
  };
  daily_cap: number;
  sent_today: number;
  worker_heartbeat_at: string | null;
}

/** GET /v1/leads/{lead_ref} event rows (EventRepository::timeline). */
interface LeadEventDto {
  event_type: string;
  template_id: string | null;
  link_id: string | null;
  event_at: string;
  actor: string;
  detail: string | null;
}

/** api/crm.yaml Contact. */
interface ContactDto {
  id: number;
  company_id: number | null;
  first_name: string | null;
  last_name: string | null;
  email: string | null;
  source: string | null;
  source_attribution: {
    lead_ref?: string;
    campaign?: string | null;
    source_directory?: string;
    score?: { fit_score?: number | null } | null;
  } | null;
  status: 'ACTIVE' | 'ARCHIVED';
  created_at: string;
  updated_at: string;
}

interface CompanyDto {
  id: number;
  name: string;
  domain: string | null;
}

interface StageDto {
  stage_key: string;
  label: string;
  position: number;
  is_terminal: boolean;
  outcome: 'won' | 'lost' | null;
}

interface OpportunityDto {
  id: number;
  contact_id: number;
  company_id: number | null;
  stage: string; // stage_key (denormalised)
  value_pence: number | null;
  currency: string;
  owner_user: string;
  status: 'open' | 'won' | 'lost';
  created_at: string;
}

/** Cursor page envelope (api-contracts: items + next_cursor). */
interface PageDto<T> {
  items: T[];
  next_cursor: string | null;
}

/** api/seo.yaml Project + spend. */
interface SeoProjectDto {
  id: number;
  domain: string;
  display_name: string;
  status: 'active' | 'paused' | 'archived';
  created_at: string;
}

/** api/seo.yaml Keyword — the full contract shape. NOTE: the contract carries
 *  NO search-volume / CPC metrics; screens must not render those columns. */
interface SeoKeywordDto {
  id: number;
  project_id: number;
  keyword: string;
  location_code?: number | null;
  language?: string | null;
  created_at?: string | null;
}

/** api/seo.yaml Ranking — append-only observation, newest first.
 *  position null = checked but not found within the checked depth. */
interface SeoRankingDto {
  id: number;
  project_id: number;
  keyword_id: number;
  checked_at: string;
  position: number | null;
  serp_url: string | null;
  source: string;
}

interface SeoSpendDto {
  month: string;
  spent_micro_pence: number;
  budget_pence: number;
  currency: 'GBP';
}

/** api/seo.yaml Audit — stored site-health result. `result` is the provider
 *  payload exactly as captured (shape varies by audit_type); `source` is the
 *  honest attribution (google_psi | crux_field | dataforseo_onpage). */
interface SeoAuditDto {
  id: number;
  project_id: number;
  audit_type: 'psi_lab' | 'crux_field' | 'onpage';
  result: Record<string, unknown> | null;
  source: string;
  captured_at: string;
}

/** api/seo.yaml CompetitorSnapshot — provider overview document as captured. */
interface SeoCompetitorSnapshotDto {
  id: number;
  project_id: number;
  competitor_domain: string;
  snapshot: Record<string, unknown> | null;
  source: string;
  captured_at: string;
}

/* ============================================================================
 * Adapters — real API shapes → the existing UI types. Honest by construction:
 * missing data becomes null / an explicit "no score yet" evidence, never a
 * fabricated value.
 * ========================================================================== */

const API_TO_UI_STATUS: Record<string, LeadStatus> = {
  DISCOVERED: 'DISCOVERED',
  QUALIFIED: 'ENRICHED',
  CONTACTED: 'CONTACTED',
  FOLLOWUP_DUE: 'CONTACTED',
  REPLIED_INTERESTED: 'REPLIED',
  REPLIED_NOT_INTERESTED: 'REPLIED',
  DEMO_BOOKED: 'REPLIED',
  SIGNED_UP: 'PROMOTED',
  CONVERTED_TENANT: 'PROMOTED',
  BOUNCED_INVALID: 'SUPPRESSED',
  OPTED_OUT: 'SUPPRESSED',
  SUPPRESSED: 'SUPPRESSED',
  ERASED: 'SUPPRESSED',
};

function humanize(key: string): string {
  return key.replace(/_/g, ' ').replace(/^./, (c) => c.toUpperCase());
}

/** Compact, faithful rendering of one free-form evidence entry. */
function evidenceEntryDetail(value: unknown): string {
  if (value === null || value === undefined) return 'no detail recorded';
  if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
    return String(value);
  }
  if (typeof value === 'object') {
    const v = value as Record<string, unknown>;
    const parts: string[] = [];
    if (typeof v.reason === 'string') parts.push(v.reason);
    if (typeof v.detail === 'string') parts.push(v.detail);
    if (v.value !== undefined && v.value !== null) parts.push(`value: ${String(v.value)}`);
    if (Array.isArray(v.signals) && v.signals.length) parts.push(`signals: ${v.signals.join(', ')}`);
    if (parts.length) return parts.join(' · ');
    try {
      const json = JSON.stringify(value);
      return json.length > 140 ? `${json.slice(0, 140)}…` : json;
    } catch {
      return 'unreadable evidence entry';
    }
  }
  return String(value);
}

/**
 * ScoreSet → UI Evidence. The scorer publishes per-field reasoning but no
 * factor weights, so factors are shown with EQUAL display weights and the
 * method string says so — transparent, not invented.
 */
function scoreSetToEvidence(s: ScoreSetDto, observedAt: string): Evidence {
  const entries = Object.entries(s.evidence ?? {});
  const n = Math.max(entries.length, 1);
  return {
    method: `Lead scoring ${s.scoring_version}${
      s.is_manual_override ? ' · manual operator override' : ''
    } — factors shown with equal display weights (scorer does not publish weights)`,
    confidence: s.is_manual_override ? 'high' : 'medium',
    version: s.scoring_version,
    updated_at: observedAt,
    factors: entries.map(([key, value]) => ({
      label: humanize(key),
      weight: 1 / n,
      detail: evidenceEntryDetail(value),
      source: s.is_manual_override ? 'operator override' : 'sales-engine scorer',
      observed_at: observedAt,
    })),
  };
}

/** Honest placeholder evidence for an unscored lead (fit_score stays null). */
function noScoreEvidence(observedAt: string): Evidence {
  return {
    method: 'Not scored yet — the scoring worker has not assessed this lead',
    confidence: 'low',
    version: '—',
    updated_at: observedAt,
    factors: [],
  };
}

function adaptLead(dto: LeadDto): Lead {
  const observed = dto.last_activity_at || dto.created_at;
  const scores = dto.scores ?? null;
  const priorityScore = scores?.priority_score ?? null;
  return {
    id: dto.lead_ref,
    business_name: dto.business_name || dto.lead_ref,
    practitioner: dto.practitioner_name ?? '',
    modality: dto.modality ?? '',
    location: [dto.city_region, dto.country].filter(Boolean).join(', '),
    email: dto.business_email ?? '',
    website: dto.website ?? '',
    status: API_TO_UI_STATUS[dto.status] ?? 'DISCOVERED',
    campaign: dto.campaign ?? '',
    next_action: dto.next_action ?? '',
    owner: dto.owner ?? '',
    fit_score: scores?.fit_score ?? null,
    fit_evidence: scores ? scoreSetToEvidence(scores, observed) : noScoreEvidence(observed),
    priority: priorityScore === null ? 'low' : priorityScore >= 70 ? 'high' : priorityScore >= 40 ? 'medium' : 'low',
    updated_at: observed,
  };
}

const API_TO_UI_EVENT: Record<string, LeadEvent['type']> = {
  EMAIL_SENT: 'SENT',
  EMAIL_OPENED: 'OPENED',
  LINK_CLICKED: 'CLICKED',
  REPLIED: 'REPLIED',
  BOUNCED: 'BOUNCED',
  PROMOTED: 'PROMOTED',
  MANUAL_NOTE: 'NOTE',
};

function adaptLeadEvents(rows: LeadEventDto[]): LeadEvent[] {
  return rows.map((row, i) => ({
    id: `ev_${i}`,
    type: API_TO_UI_EVENT[row.event_type] ?? 'NOTE',
    at: row.event_at,
    detail:
      row.detail ||
      [humanize(row.event_type.toLowerCase()), row.template_id].filter(Boolean).join(' · '),
  }));
}

function attributionLine(c: ContactDto): string | undefined {
  const a = c.source_attribution;
  if (!a) return c.source ?? undefined;
  const fit = a.score?.fit_score;
  return ['Outreach', a.lead_ref, a.campaign, fit != null ? `fit ${fit}` : null]
    .filter(Boolean)
    .join(' · ');
}

function adaptContact(dto: ContactDto, companies: Map<number, string>): Contact {
  const name =
    [dto.first_name, dto.last_name].filter(Boolean).join(' ') || dto.email || `Contact #${dto.id}`;
  return {
    id: String(dto.id),
    name,
    email: dto.email ?? '',
    company: (dto.company_id != null && companies.get(dto.company_id)) || '',
    title: '—', // not carried by the CRM API yet
    stage: dto.status === 'ARCHIVED' ? 'Archived' : 'Active',
    owner: '—', // not carried by the CRM API yet
    source_attribution: attributionLine(dto),
  };
}

/** Fixed v1 stage keys → the UI PipelineStage labels (keys are immutable). */
const STAGE_KEY_TO_UI: Record<string, PipelineStage> = {
  new: 'New',
  contacted: 'Contacted',
  qualified: 'Qualified',
  proposal: 'Proposal',
  won: 'Won',
  lost: 'Lost',
};

function adaptOpportunity(
  dto: OpportunityDto,
  contacts: Map<number, string>,
  companies: Map<number, string>,
): Opportunity {
  const companyName = (dto.company_id != null && companies.get(dto.company_id)) || '';
  const contactName = contacts.get(dto.contact_id) ?? '';
  return {
    id: String(dto.id),
    name: companyName || contactName || `Opportunity #${dto.id}`,
    company: companyName || contactName,
    value: dto.value_pence != null ? dto.value_pence / 100 : 0,
    currency: dto.currency || 'GBP',
    stage: STAGE_KEY_TO_UI[dto.stage] ?? 'New',
    owner: dto.owner_user || '—',
  };
}

function adaptSeoProject(dto: SeoProjectDto, keywordCount: number | null): SeoProject {
  return {
    id: String(dto.id),
    domain: dto.display_name ? `${dto.display_name} · ${dto.domain}` : dto.domain,
    tracked_keywords: keywordCount,
    avg_position: null, // no aggregate endpoint yet — rendered as "no data yet"
    visibility_score: null, // no visibility model in the API yet
    monthly_spend: null, // spend is workspace-level (/seo/spend), not per-project
    spend_cap: null,
    health: null,
    status_label: dto.status,
  };
}

/* ============================================================================
 * Surface sources — one gated fetcher per screen.
 * ========================================================================== */

// ---- Sales: leads list (/sales/leads) --------------------------------------
// Real: GET /v1/summary → recent_leads (the API's only list read — 25 newest,
// each with its latest ScoreSet). A dedicated cursor-paged list endpoint does
// not exist yet; when it lands, only this function changes.
export async function fetchLeads(projectId?: number): Promise<Lead[]> {
  if (USE_MOCK_DATA) return MOCK_LEADS;
  // Domain scope: when a domain is being "Viewed" we pass its project_id so the
  // API returns ONLY that domain's leads (host-matched). No project_id => the
  // explicit workspace-wide ("All domains") lens. Never another domain's leads.
  const summary = await api.get<SummaryDto>('/summary', projectId ? { project_id: projectId } : undefined);
  return (summary.recent_leads ?? []).map(adaptLead);
}

// ---- Sales: lead detail (/sales/leads/:id — :id is the lead_ref) -----------
export interface LeadDetail {
  lead: Lead;
  events: LeadEvent[];
}

export async function fetchLeadDetail(id: string): Promise<LeadDetail | null> {
  if (USE_MOCK_DATA) {
    const lead = MOCK_LEADS.find((l) => l.id === id);
    return lead ? { lead, events: MOCK_LEAD_EVENTS[id] ?? [] } : null;
  }
  let res: { lead: LeadDto; events: LeadEventDto[] };
  try {
    res = await api.get<{ lead: LeadDto; events: LeadEventDto[] }>(
      `/leads/${encodeURIComponent(id)}`,
    );
  } catch (err) {
    if (err instanceof ApiError && err.status === 404) return null; // → "Lead not found"
    throw err;
  }
  if (!res?.lead) return null;
  return { lead: adaptLead(res.lead), events: adaptLeadEvents(res.events ?? []) };
}

// ---- CRM: contacts (/sales/crm/contacts) ------------------------------------
export async function fetchContacts(projectId?: number): Promise<Contact[]> {
  if (USE_MOCK_DATA) return MOCK_CONTACTS;
  // project_id is validated server-side (foreign => 422) but contacts have no
  // per-row domain attribution, so they stay workspace-scoped ("Across
  // workspace" — surfaced by the page label). We still thread it so a domain
  // switch invalidates + refetches (no stale previous-domain view).
  const [contacts, companies] = await Promise.all([
    api.get<PageDto<ContactDto>>('/crm/contacts', { limit: 200, ...(projectId ? { project_id: projectId } : {}) }),
    api.get<PageDto<CompanyDto>>('/crm/companies', { limit: 200 }),
  ]);
  const companyNames = new Map((companies.items ?? []).map((c) => [c.id, c.name]));
  return (contacts.items ?? []).map((c) => adaptContact(c, companyNames));
}

// ---- CRM: pipeline (/sales/pipeline) ----------------------------------------
export interface PipelineColumn {
  key: string;
  label: string;
  opportunities: Opportunity[];
}

export async function fetchPipeline(): Promise<PipelineColumn[]> {
  if (USE_MOCK_DATA) {
    const order: PipelineStage[] = ['New', 'Contacted', 'Qualified', 'Proposal', 'Won', 'Lost'];
    return order.map((label) => ({
      key: label.toLowerCase(),
      label,
      opportunities: MOCK_OPPORTUNITIES.filter((o) => o.stage === label),
    }));
  }
  const [stages, opps, contacts, companies] = await Promise.all([
    api.get<{ stages: StageDto[] }>('/crm/stages'),
    api.get<PageDto<OpportunityDto>>('/crm/opportunities', { limit: 200 }),
    api.get<PageDto<ContactDto>>('/crm/contacts', { limit: 200 }),
    api.get<PageDto<CompanyDto>>('/crm/companies', { limit: 200 }),
  ]);
  const contactNames = new Map(
    (contacts.items ?? []).map((c) => [
      c.id,
      [c.first_name, c.last_name].filter(Boolean).join(' ') || c.email || `Contact #${c.id}`,
    ]),
  );
  const companyNames = new Map((companies.items ?? []).map((c) => [c.id, c.name]));
  const adapted = (opps.items ?? []).map((o) => ({
    stageKey: o.stage,
    opp: adaptOpportunity(o, contactNames, companyNames),
  }));
  const ordered = [...(stages.stages ?? [])].sort((a, b) => a.position - b.position);
  return ordered.map((s) => ({
    key: s.stage_key,
    label: s.label || STAGE_KEY_TO_UI[s.stage_key] || humanize(s.stage_key),
    opportunities: adapted.filter((a) => a.stageKey === s.stage_key).map((a) => a.opp),
  }));
}

// ---- SEO: projects (/seo/projects) ------------------------------------------
export interface SeoOverview {
  projects: SeoProject[];
  /** Workspace-level provider spend (GET /v1/seo/spend); null if unavailable. */
  spend: { spent: number; budget: number } | null;
  /** Data-derived insight strip; empty in real mode until real signals exist. */
  insights: Array<{ title: string; confidence: 'low' | 'medium' | 'high'; body: string }>;
}

export async function fetchSeoOverview(): Promise<SeoOverview> {
  if (USE_MOCK_DATA) {
    return {
      projects: MOCK_SEO_PROJECTS,
      spend: { spent: 35.2, budget: 50 },
      insights: [
        {
          title: 'Rankings holding',
          confidence: 'medium',
          body: 'grow2gether.com steady in top 10 for 9 of 42 tracked terms this week.',
        },
        {
          title: 'Spend nearing cap',
          confidence: 'high',
          body: 'booksesh.com at £22.80 of £25 — tracking pauses at cap to protect budget.',
        },
        {
          title: 'Keyword gap found',
          confidence: 'high',
          body: '“mobile pilates cardiff” — competitor at #3, you are unranked. Content brief ready.',
        },
      ],
    };
  }

  const page = await api.get<{ projects: SeoProjectDto[]; next_cursor: string | null }>(
    '/seo/projects',
    { limit: 200 },
  );
  const projectDtos = page.projects ?? [];

  // Keyword counts: one cheap call per project (fine at current scale; a
  // counts aggregate on the API would replace this).
  const keywordCounts = await Promise.all(
    projectDtos.map(async (p) => {
      try {
        const kw = await api.get<{ keywords: SeoKeywordDto[]; next_cursor: string | null }>(
          `/seo/projects/${p.id}/keywords`,
          { limit: 200 },
        );
        return (kw.keywords ?? []).length;
      } catch {
        return null; // keyword read failed — show "no data", don't fail the page
      }
    }),
  );

  let spend: SeoOverview['spend'] = null;
  try {
    const s = await api.get<SeoSpendDto>('/seo/spend');
    spend = { spent: s.spent_micro_pence / 1_000_000 / 100, budget: s.budget_pence / 100 };
  } catch {
    spend = null;
  }

  const insights: SeoOverview['insights'] = [];
  if (spend && spend.budget > 0 && spend.spent / spend.budget >= 0.85) {
    insights.push({
      title: 'Spend nearing cap',
      confidence: 'high',
      body: `${formatCurrency(spend.spent)} of ${formatCurrency(spend.budget)} monthly budget used — provider calls pause at the cap.`,
    });
  }

  return {
    projects: projectDtos.map((p, i) => adaptSeoProject(p, keywordCounts[i] ?? null)),
    spend,
    insights,
  };
}

// ---- SEO: project options (/seo/keywords selector) ---------------------------
export interface SeoProjectOption {
  id: string;
  /** display_name · domain when a display name exists, else the bare domain. */
  label: string;
  domain: string;
  status: string;
}

const MOCK_SEO_PROJECT_OPTIONS: SeoProjectOption[] = [
  { id: 'seo1', label: 'grow2gether.com', domain: 'grow2gether.com', status: 'active' },
  { id: 'seo2', label: 'booksesh.com', domain: 'booksesh.com', status: 'active' },
];

export async function fetchSeoProjectOptions(): Promise<SeoProjectOption[]> {
  if (USE_MOCK_DATA) return MOCK_SEO_PROJECT_OPTIONS;
  const page = await api.get<{ projects: SeoProjectDto[]; next_cursor: string | null }>(
    '/seo/projects',
    { limit: 200 },
  );
  return (page.projects ?? []).map((p) => ({
    id: String(p.id),
    label: p.display_name ? `${p.display_name} · ${p.domain}` : p.domain,
    domain: p.domain,
    status: p.status,
  }));
}

// ---- SEO: keywords + latest rank (/seo/keywords) ------------------------------
export interface SeoKeywordRank {
  /** null = checked but not found within the checked depth (contract). */
  position: number | null;
  checked_at: string;
  /** Honest attribution — always present on stored observations. */
  source: string;
  serp_url: string | null;
}

export interface SeoKeywordRow {
  id: string;
  keyword: string;
  /** DataForSEO location id (2826 = United Kingdom); null if not returned. */
  location_code: number | null;
  language: string | null;
  created_at: string | null;
  /** Latest rank observation; null = no rank check has ever run. */
  latest_rank: SeoKeywordRank | null;
}

const MOCK_SEO_KEYWORD_ROWS: Record<string, SeoKeywordRow[]> = {
  seo1: [
    {
      id: 'kw1', keyword: 'community growth platform', location_code: 2826, language: 'en',
      created_at: '2026-06-02T09:00:00Z',
      latest_rank: { position: 4, checked_at: '2026-07-13T06:00:00Z', source: 'dataforseo_serp', serp_url: 'https://grow2gether.com/' },
    },
    {
      id: 'kw2', keyword: 'peer support groups online', location_code: 2826, language: 'en',
      created_at: '2026-06-02T09:00:00Z',
      latest_rank: { position: 11, checked_at: '2026-07-13T06:00:00Z', source: 'dataforseo_serp', serp_url: 'https://grow2gether.com/groups' },
    },
    {
      id: 'kw3', keyword: 'group coaching software uk', location_code: 2826, language: 'en',
      created_at: '2026-06-15T09:00:00Z',
      latest_rank: { position: null, checked_at: '2026-07-12T06:00:00Z', source: 'dataforseo_serp', serp_url: null },
    },
    {
      id: 'kw4', keyword: 'community engagement app', location_code: 2826, language: 'en',
      created_at: '2026-07-10T09:00:00Z', latest_rank: null,
    },
  ],
  seo2: [
    {
      id: 'kw5', keyword: 'booking software for therapists', location_code: 2826, language: 'en',
      created_at: '2026-05-20T09:00:00Z',
      latest_rank: { position: 9, checked_at: '2026-07-13T06:05:00Z', source: 'dataforseo_serp', serp_url: 'https://booksesh.com/' },
    },
    {
      id: 'kw6', keyword: 'online booking system uk', location_code: 2826, language: 'en',
      created_at: '2026-05-20T09:00:00Z',
      latest_rank: { position: 23, checked_at: '2026-07-13T06:05:00Z', source: 'dataforseo_serp', serp_url: 'https://booksesh.com/features' },
    },
    {
      id: 'kw7', keyword: 'mobile pilates cardiff', location_code: 2826, language: 'en',
      created_at: '2026-07-08T09:00:00Z', latest_rank: null,
    },
  ],
};

function adaptSeoKeyword(dto: SeoKeywordDto, latest: SeoRankingDto | undefined): SeoKeywordRow {
  return {
    id: String(dto.id),
    keyword: dto.keyword,
    location_code: dto.location_code ?? null,
    language: dto.language ?? null,
    created_at: dto.created_at ?? null,
    latest_rank: latest
      ? {
          position: latest.position,
          checked_at: latest.checked_at,
          source: latest.source,
          serp_url: latest.serp_url,
        }
      : null,
  };
}

/**
 * Tracked keywords for one project, each joined with its latest rank
 * observation (GET keywords + GET rankings, newest first). A rankings read
 * failure degrades to "no rank data" rather than failing the whole screen.
 */
export async function fetchSeoKeywords(projectId: string): Promise<SeoKeywordRow[]> {
  if (USE_MOCK_DATA) return MOCK_SEO_KEYWORD_ROWS[projectId] ?? [];
  const pid = encodeURIComponent(projectId);
  const [kwPage, rankPage] = await Promise.all([
    api.get<{ keywords: SeoKeywordDto[]; next_cursor: string | null }>(
      `/seo/projects/${pid}/keywords`,
      { limit: 200 },
    ),
    api
      .get<{ rankings: SeoRankingDto[]; next_cursor: string | null }>(
        `/seo/projects/${pid}/rankings`,
        { limit: 200 },
      )
      .catch(() => null), // rank history unavailable → keywords still render
  ]);
  const latestByKeyword = new Map<number, SeoRankingDto>();
  for (const r of rankPage?.rankings ?? []) {
    if (!latestByKeyword.has(r.keyword_id)) latestByKeyword.set(r.keyword_id, r); // newest first
  }
  return (kwPage.keywords ?? []).map((k) => adaptSeoKeyword(k, latestByKeyword.get(k.id)));
}

// ---- SEO: site-health audits (/seo/site-health) --------------------------------
export type SeoAuditType = 'psi_lab' | 'crux_field' | 'onpage';

export interface SeoAuditRow {
  id: string;
  audit_type: SeoAuditType;
  /** Stored provider payload exactly as captured — rendered, never reshaped.
   *  `no_data: true` inside = an explicit "Google has no field data" outcome. */
  result: Record<string, unknown>;
  /** Honest attribution — google_psi | crux_field | dataforseo_onpage. */
  source: string;
  captured_at: string;
}

const MOCK_SEO_AUDIT_ROWS: Record<string, SeoAuditRow[]> = {
  seo1: [
    {
      id: 'aud1',
      audit_type: 'psi_lab',
      result: {
        url: 'https://grow2gether.com/',
        scores: { performance: 87, accessibility: 96, best_practices: 100, seo: 92 },
        lcp_ms: 2140,
        cls: 0.04,
        tbt_ms: 180,
      },
      source: 'google_psi',
      captured_at: '2026-07-13T07:20:00Z',
    },
    {
      id: 'aud2',
      audit_type: 'crux_field',
      result: { origin: 'https://grow2gether.com', p75: { lcp_ms: 2450, inp_ms: 210, cls: 0.07 } },
      source: 'crux_field',
      captured_at: '2026-07-10T07:00:00Z',
    },
    {
      id: 'aud3',
      audit_type: 'onpage',
      result: {
        url: 'https://grow2gether.com/',
        onpage_score: 88.6,
        pages_crawled: 10,
        issues_found: 7,
      },
      source: 'dataforseo_onpage',
      captured_at: '2026-07-08T09:30:00Z',
    },
  ],
  seo2: [
    {
      id: 'aud4',
      audit_type: 'psi_lab',
      result: {
        url: 'https://booksesh.com/',
        scores: { performance: 74, accessibility: 91, best_practices: 96, seo: 89 },
        lcp_ms: 3010,
        cls: 0.11,
        tbt_ms: 420,
      },
      source: 'google_psi',
      captured_at: '2026-07-12T18:05:00Z',
    },
    {
      // Real-world case the UI must render honestly: CrUX has no field data
      // for this origin — stored as an explicit no-data outcome, never blank.
      id: 'aud5',
      audit_type: 'crux_field',
      result: { origin: 'https://booksesh.com', no_data: true },
      source: 'crux_field',
      captured_at: '2026-07-12T18:06:00Z',
    },
  ],
};

/** Stored site-health results for one project, newest first (contract order). */
export async function fetchSeoAudits(projectId: string): Promise<SeoAuditRow[]> {
  if (USE_MOCK_DATA) return MOCK_SEO_AUDIT_ROWS[projectId] ?? [];
  const page = await api.get<{ audits: SeoAuditDto[]; next_cursor: string | null }>(
    `/seo/projects/${encodeURIComponent(projectId)}/audits`,
    { limit: 200 },
  );
  return (page.audits ?? []).map((a) => ({
    id: String(a.id),
    audit_type: a.audit_type,
    result: a.result ?? {},
    source: a.source,
    captured_at: a.captured_at,
  }));
}

export interface SeoAuditRunResult {
  audit_id: number | null;
  audit_type: string;
  source: string;
  /** true = served from the freshness cache (HTTP 200 — no provider call,
   *  no rate cost); false = fresh provider run stored (HTTP 201). The body
   *  field is the contract's own discriminator, so no status sniffing needed. */
  cached: boolean;
  /** When a cache-served result was originally captured (200 responses). */
  captured_at: string | null;
  /** true = crux_field outcome where Google has no field data (explicit, stored). */
  no_data: boolean;
}

/**
 * Run one site-health audit now (POST /v1/seo/projects/{id}/audits). onpage is
 * paid + budget-guarded; psi_lab / crux_field are free but ALL provider runs
 * count against the 10-per-24h workspace cap. Callers branch on ApiError:
 * 429 rate_limited, 402 budget_exceeded (onpage), 502 provider_error (nothing
 * stored), 503 provider_unavailable. `force: true` bypasses the freshness
 * cache (still counts against the cap).
 */
export async function runSeoAudit(
  projectId: string,
  input: { audit_type: SeoAuditType; force?: boolean },
): Promise<SeoAuditRunResult> {
  if (USE_MOCK_DATA) {
    const source =
      input.audit_type === 'psi_lab'
        ? 'google_psi'
        : input.audit_type === 'crux_field'
          ? 'crux_field'
          : 'dataforseo_onpage';
    return {
      audit_id: Math.floor(Math.random() * 1_000_000),
      audit_type: input.audit_type,
      source,
      cached: false,
      captured_at: new Date().toISOString(),
      no_data: false,
    };
  }
  const body: Record<string, unknown> = { audit_type: input.audit_type };
  if (input.force) body.force = true;
  const res = await api.post<{
    audit_id: number | null;
    audit_type: string;
    source: string;
    cached?: boolean;
    captured_at?: string | null;
    no_data?: boolean;
  }>(`/seo/projects/${encodeURIComponent(projectId)}/audits`, body);
  return {
    audit_id: res.audit_id ?? null,
    audit_type: res.audit_type,
    source: res.source,
    cached: res.cached === true,
    captured_at: res.captured_at ?? null,
    no_data: res.no_data === true,
  };
}

// ---- SEO: competitor snapshots (/seo/competitors) ------------------------------
export interface SeoCompetitorRow {
  id: string;
  competitor_domain: string;
  /** Provider overview document (JSON) exactly as captured. */
  snapshot: Record<string, unknown>;
  /** Honest attribution — e.g. dataforseo_labs. */
  source: string;
  captured_at: string;
}

const MOCK_SEO_COMPETITOR_ROWS: Record<string, SeoCompetitorRow[]> = {
  seo1: [
    {
      id: 'comp1',
      competitor_domain: 'circle.so',
      snapshot: {
        target: 'grow2gether.com',
        items: [
          { domain: 'circle.so', avg_position: 6.2, intersections: 14 },
          { domain: 'mightynetworks.com', avg_position: 9.8, intersections: 11 },
          { domain: 'heartbeat.chat', avg_position: 14.1, intersections: 6 },
        ],
      },
      source: 'dataforseo_labs',
      captured_at: '2026-07-11T10:12:00Z',
    },
  ],
  seo2: [
    {
      id: 'comp2',
      competitor_domain: 'fresha.com',
      snapshot: {
        target: 'booksesh.com',
        items: [
          { domain: 'fresha.com', avg_position: 3.4, intersections: 21 },
          { domain: 'setmore.com', avg_position: 7.9, intersections: 12 },
        ],
      },
      source: 'dataforseo_labs',
      captured_at: '2026-07-09T15:40:00Z',
    },
  ],
};

/** Stored competitor snapshots for one project, newest first (contract order). */
export async function fetchSeoCompetitors(projectId: string): Promise<SeoCompetitorRow[]> {
  if (USE_MOCK_DATA) return MOCK_SEO_COMPETITOR_ROWS[projectId] ?? [];
  const page = await api.get<{ competitors: SeoCompetitorSnapshotDto[]; next_cursor: string | null }>(
    `/seo/projects/${encodeURIComponent(projectId)}/competitors`,
    { limit: 200 },
  );
  return (page.competitors ?? []).map((c) => ({
    id: String(c.id),
    competitor_domain: c.competitor_domain,
    snapshot: c.snapshot ?? {},
    source: c.source,
    captured_at: c.captured_at,
  }));
}

/**
 * Capture one competitor snapshot now (paid — rows are priced up-front and
 * budget-guarded server-side). 201 → { snapshot_id, source, items }. Callers
 * branch on ApiError: 402 budget_exceeded (nothing spent), 502 provider_error
 * (nothing stored), 503 provider_unavailable (no provider wired).
 */
export async function captureSeoCompetitorSnapshot(
  projectId: string,
  input: { competitor_domain: string; limit?: number },
): Promise<{ snapshot_id: number | null; source: string; items: number }> {
  if (USE_MOCK_DATA) {
    return {
      snapshot_id: Math.floor(Math.random() * 1_000_000),
      source: 'dataforseo_labs',
      items: input.limit ?? 10,
    };
  }
  const body: Record<string, unknown> = { competitor_domain: input.competitor_domain };
  if (input.limit !== undefined) body.limit = input.limit;
  const res = await api.post<{ snapshot_id: number | null; source: string; items: number }>(
    `/seo/projects/${encodeURIComponent(projectId)}/competitors`,
    body,
  );
  return {
    snapshot_id: res.snapshot_id ?? null,
    source: res.source,
    items: res.items ?? 0,
  };
}

// ---- SEO: Google Search Console search analytics (/seo/search-performance) ----
// G1 contracts: GET /v1/seo/projects/{id}/search-analytics (stored rows +
// last_synced_at), POST .../search-analytics/sync (pull GSC last 28d + store
// ONE top-opportunity insight), GET /v1/seo/intelligence (workspace rollup).
// Every stored row is source-attributed ("gsc") — never a proprietary-index
// claim, never invented numbers.

/** Stored GSC query row (bc_seo_search_analytics). */
export interface SeoSearchQueryRow {
  query: string;
  clicks: number;
  impressions: number;
  /** click-through rate as a fraction (0.042 = 4.2%), as GSC reports it. */
  ctr: number;
  /** average position for the query (1 = top result). */
  position: number;
  date_start: string;
  date_end: string;
  /** honest attribution — "gsc" on stored rows. */
  source: string;
}

export interface SeoSearchAnalytics {
  rows: SeoSearchQueryRow[];
  /** null = this project has never synced from Search Console. */
  last_synced_at: string | null;
}

/** Row DTO as served (numerics may arrive as DB-decimal strings — coerced). */
interface SeoSearchQueryRowDto {
  query: string;
  clicks: number | string;
  impressions: number | string;
  ctr: number | string;
  position: number | string;
  date_start: string;
  date_end: string;
  source?: string;
}

function adaptSeoSearchRow(dto: SeoSearchQueryRowDto): SeoSearchQueryRow {
  return {
    query: dto.query,
    clicks: Number(dto.clicks) || 0,
    impressions: Number(dto.impressions) || 0,
    ctr: Number(dto.ctr) || 0,
    position: Number(dto.position) || 0,
    date_start: dto.date_start,
    date_end: dto.date_end,
    source: dto.source ?? 'gsc',
  };
}

/** Stored search-analytics rows for one project (top queries, last window). */
export async function fetchSeoSearchAnalytics(projectId: string): Promise<SeoSearchAnalytics> {
  if (USE_MOCK_DATA) {
    return MOCK_SEO_SEARCH_ANALYTICS[projectId] ?? { rows: [], last_synced_at: null };
  }
  const res = await api.get<{ rows: SeoSearchQueryRowDto[]; last_synced_at: string | null }>(
    `/seo/projects/${encodeURIComponent(projectId)}/search-analytics`,
    { limit: 100 },
  );
  return {
    rows: (res.rows ?? []).map(adaptSeoSearchRow),
    last_synced_at: res.last_synced_at ?? null,
  };
}

// ---- fix/gsc-table-filters (02-11-2026): window-fenced table read ----------
// GET .../search-analytics?window=1d|7d|28d|3m returns ONE stored window (the
// newest of that length), already aggregated by query, plus which presets have
// stored data. fetchSeoSearchAnalytics() above is unchanged.

/** A stored GSC window as the API describes it. */
export interface SeoSearchWindowMeta {
  token: string;
  start: string;
  end: string;
  synced_at: string | null;
  /** queries in the chosen window before the row cap (chosen window only). */
  total_rows?: number;
  /** queries stored in this window (availability list only). */
  rows?: number;
}

export interface SeoSearchAnalyticsWindowed extends SeoSearchAnalytics {
  /** the chosen window; null = nothing stored yet for this preset. */
  window: SeoSearchWindowMeta | null;
  /** newest stored window per preset that has data. */
  windows: SeoSearchWindowMeta[];
}

export async function fetchSeoSearchAnalyticsWindow(
  projectId: string,
  window: string,
): Promise<SeoSearchAnalyticsWindowed> {
  if (USE_MOCK_DATA) {
    const stored = MOCK_SEO_SEARCH_ANALYTICS[projectId] ?? { rows: [], last_synced_at: null };
    const first = stored.rows[0];
    const meta: SeoSearchWindowMeta | null = first
      ? { token: '28d', start: first.date_start, end: first.date_end, synced_at: stored.last_synced_at, total_rows: stored.rows.length }
      : null;
    return { ...stored, window: window === '28d' ? meta : null, windows: meta ? [{ ...meta, rows: stored.rows.length }] : [] };
  }
  const res = await api.get<{
    rows: SeoSearchQueryRowDto[];
    last_synced_at: string | null;
    window?: SeoSearchWindowMeta | null;
    windows?: SeoSearchWindowMeta[];
  }>(`/seo/projects/${encodeURIComponent(projectId)}/search-analytics`, { window, limit: 1000 });
  return {
    rows: (res.rows ?? []).map(adaptSeoSearchRow),
    last_synced_at: res.last_synced_at ?? null,
    window: res.window ?? null,
    windows: Array.isArray(res.windows) ? res.windows : [],
  };
}

/** One stored AI insight (bc_seo_insights) — advisory, platform-branded. */
export interface SeoGscInsight {
  /** "keyword_opportunity" | "summary" (contract kinds). */
  kind: string;
  subject: string;
  body: string;
}

export interface SeoSearchSyncResult {
  synced_rows: number;
  window: { start: string; end: string };
  /** ONE top-opportunity insight generated server-side; null when no rows
   *  (the contract skips the insight rather than inventing one). */
  insight: SeoGscInsight | null;
  no_data: boolean;
}

/**
 * Pull the last 28 days of Search Console query data now (POST
 * /v1/seo/projects/{id}/search-analytics/sync). Fail-closed server-side:
 * callers branch on ApiError — 503 provider_unavailable = Search Console is
 * not connected for this environment (nothing stored), 502 provider_error =
 * the GSC request failed (nothing stored), 429 rate_limited. Idempotency
 * header is added by the api client on every mutation.
 */
export async function syncSeoSearchAnalytics(
  projectId: string,
  window?: string,
): Promise<SeoSearchSyncResult> {
  if (USE_MOCK_DATA) {
    const stored = MOCK_SEO_SEARCH_ANALYTICS[projectId] ?? { rows: [], last_synced_at: null };
    const first = stored.rows[0];
    return {
      synced_rows: stored.rows.length,
      window: first
        ? { start: first.date_start, end: first.date_end }
        : { start: '2026-06-16', end: '2026-07-14' },
      insight: MOCK_SEO_GSC_INSIGHTS[projectId] ?? null,
      no_data: stored.rows.length === 0,
    };
  }
  const res = await api.post<{
    synced_rows: number;
    window: { start: string; end: string };
    insight: { kind: string; subject: string; body: string } | null;
    no_data?: boolean;
  }>(
    `/seo/projects/${encodeURIComponent(projectId)}/search-analytics/sync`,
    window ? { window } : undefined,
  );
  return {
    synced_rows: res.synced_rows ?? 0,
    window: res.window ?? { start: '', end: '' },
    insight: res.insight ?? null,
    no_data: res.no_data === true,
  };
}

// ---- SEO: workspace intelligence rollup (GET /v1/seo/intelligence) -----------
export interface SeoIntelligenceOpportunity {
  /** stringified for UI comparison against project option ids. */
  project_id: string;
  domain: string;
  subject: string;
  position: number;
  impressions: number;
  clicks: number;
  body: string;
}

export interface SeoIntelligence {
  gsc_connected: boolean;
  properties: number;
  last_synced_at: string | null;
  opportunities: number;
  top_opportunity: SeoIntelligenceOpportunity | null;
}

/** Workspace SEO intelligence rollup — nulls stay null, never fabricated. */
export async function fetchSeoIntelligence(): Promise<SeoIntelligence> {
  if (USE_MOCK_DATA) return MOCK_SEO_INTELLIGENCE;
  const res = await api.get<{
    gsc_connected: boolean;
    properties: number;
    last_synced_at: string | null;
    opportunities: number;
    top_opportunity: {
      project_id: number | string;
      domain: string;
      subject: string;
      position: number | string;
      impressions: number | string;
      clicks: number | string;
      body: string;
    } | null;
  }>('/seo/intelligence');
  return {
    gsc_connected: res.gsc_connected === true,
    properties: Number(res.properties) || 0,
    last_synced_at: res.last_synced_at ?? null,
    opportunities: Number(res.opportunities) || 0,
    top_opportunity: res.top_opportunity
      ? {
          project_id: String(res.top_opportunity.project_id),
          domain: res.top_opportunity.domain,
          subject: res.top_opportunity.subject,
          position: Number(res.top_opportunity.position) || 0,
          impressions: Number(res.top_opportunity.impressions) || 0,
          clicks: Number(res.top_opportunity.clicks) || 0,
          body: res.top_opportunity.body,
        }
      : null,
  };
}

/* ============================================================================
 * SEO Domain Intelligence — W1a (locked W1a CONTRACTS §"Locked API contract").
 * Four read surfaces over DataForSEO Labs (paid) + stored rankings (free) +
 * a GSC-vs-provider directional benchmark. Null-honest by construction: a
 * metric the provider omits stays null (numOrNull never fabricates a zero),
 * and every envelope carries its own source attribution.
 * ========================================================================== */

/** Coerce a possibly string/absent numeric to number|null WITHOUT inventing a
 *  zero — absent / blank / non-numeric all stay null (never a fabricated value).
 *  Distinct from the GSC adapters' `Number(x) || 0`, where 0 is a real metric. */
function numOrNull(v: unknown): number | null {
  if (v === null || v === undefined || v === '') return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

/** Non-empty string or null — never coerces to a fabricated value. */
function strOrNull(v: unknown): string | null {
  return typeof v === 'string' && v !== '' ? v : null;
}

// ---- SEO: domain overview (GET /seo/projects/{id}/domain-overview) -----------
// PAID (DataForSEO Labs), 7d cache. Fetched ONLY on explicit user action.
export interface DomainOverviewResult {
  /** null = provider returned no overview (an honest empty, not an error). */
  overview: DomainOverview | null;
  source: string;
  fetched_at: string | null;
  cache_hit: boolean;
}

interface DomainOverviewDto {
  overview?: {
    organic_keywords?: number | string | null;
    organic_traffic?: number | string | null;
    organic_traffic_value?: number | string | null;
    rank_distribution?: Record<string, number | string | null> | null;
  } | null;
  source?: string;
  fetched_at?: string | null;
  cache_hit?: boolean;
}

const MOCK_DOMAIN_OVERVIEW: Record<string, DomainOverviewResult> = {
  seo1: {
    overview: {
      organic_keywords: 1284,
      organic_traffic: 8420,
      organic_traffic_value: 6130,
      rank_distribution: { pos_1_3: 46, pos_4_10: 173, pos_11_20: 261, pos_21_50: 512, pos_51_100: 292 },
    },
    source: 'dataforseo.labs.domain_overview',
    fetched_at: '2026-07-13T06:20:00Z',
    cache_hit: false,
  },
  seo2: {
    overview: {
      organic_keywords: 342,
      organic_traffic: 1910,
      organic_traffic_value: 2280,
      // A real null-honesty case: provider returned no distribution buckets.
      rank_distribution: null,
    },
    source: 'dataforseo.labs.domain_overview',
    fetched_at: '2026-07-12T18:10:00Z',
    cache_hit: true,
  },
};

/** Adapt one distribution map, null-honest per bucket. */
function adaptRankDistribution(
  raw: Record<string, number | string | null> | null | undefined,
): DomainOverview['rank_distribution'] {
  if (!raw || typeof raw !== 'object') return null;
  const out: Record<string, number | null> = {};
  for (const [k, v] of Object.entries(raw)) out[k] = numOrNull(v);
  return Object.keys(out).length > 0 ? out : null;
}

/**
 * Load the DataForSEO Labs domain overview for one project (PAID, 7d cache).
 * User-triggered only. Callers branch on ApiError: 503 provider_unavailable
 * (nothing fetched/charged), 402 budget_exceeded (refused pre-charge), 429
 * rate_limited, 502 provider_error (nothing stored).
 */
export async function fetchSeoDomainOverview(projectId: string): Promise<DomainOverviewResult> {
  if (USE_MOCK_DATA) {
    return (
      MOCK_DOMAIN_OVERVIEW[projectId] ?? {
        overview: null,
        source: 'dataforseo.labs.domain_overview',
        fetched_at: null,
        cache_hit: false,
      }
    );
  }
  const res = await api.get<DomainOverviewDto>(
    `/seo/projects/${encodeURIComponent(projectId)}/domain-overview`,
  );
  const o = res.overview ?? null;
  return {
    overview: o
      ? {
          organic_keywords: numOrNull(o.organic_keywords),
          organic_traffic: numOrNull(o.organic_traffic),
          organic_traffic_value: numOrNull(o.organic_traffic_value),
          rank_distribution: adaptRankDistribution(o.rank_distribution),
        }
      : null,
    source: res.source ?? 'dataforseo.labs.domain_overview',
    fetched_at: res.fetched_at ?? null,
    cache_hit: res.cache_hit === true,
  };
}

// ---- SEO: organic keywords (GET /seo/projects/{id}/organic-keywords) ---------
// PAID (DataForSEO Labs), 7d cache. Fetched ONLY on explicit user action.
export interface OrganicKeywordsResult {
  keywords: OrganicKeyword[];
  source: string;
  fetched_at: string | null;
  cache_hit: boolean;
}

interface OrganicKeywordDto {
  keyword: string;
  position?: number | string | null;
  volume?: number | string | null;
  traffic?: number | string | null;
  url?: string | null;
  difficulty?: number | string | null;
}

const MOCK_ORGANIC_KEYWORDS: Record<string, OrganicKeywordsResult> = {
  seo1: {
    keywords: [
      { keyword: 'community growth platform', position: 4, volume: 1300, traffic: 210, url: 'https://grow2gether.com/', difficulty: 38 },
      { keyword: 'peer support groups online', position: 11, volume: 880, traffic: 46, url: 'https://grow2gether.com/groups', difficulty: 44 },
      // null-honest row: provider returned no volume / difficulty for this term.
      { keyword: 'group coaching software uk', position: 27, volume: null, traffic: null, url: 'https://grow2gether.com/coaching', difficulty: null },
    ],
    source: 'dataforseo.labs.ranked_keywords',
    fetched_at: '2026-07-13T06:22:00Z',
    cache_hit: false,
  },
  seo2: {
    keywords: [
      { keyword: 'booking software for therapists', position: 9, volume: 720, traffic: 58, url: 'https://booksesh.com/', difficulty: 41 },
      { keyword: 'online booking system uk', position: 23, volume: 2400, traffic: 30, url: 'https://booksesh.com/features', difficulty: 55 },
    ],
    source: 'dataforseo.labs.ranked_keywords',
    fetched_at: '2026-07-12T18:12:00Z',
    cache_hit: true,
  },
};

/**
 * Load organic keywords the domain ranks for (PAID, 7d cache). User-triggered
 * only. `limit` caps the row count (contract default 50). Same ApiError
 * branches as the domain overview.
 */
export async function fetchSeoOrganicKeywords(
  projectId: string,
  limit = 50,
): Promise<OrganicKeywordsResult> {
  if (USE_MOCK_DATA) {
    const mock = MOCK_ORGANIC_KEYWORDS[projectId] ?? {
      keywords: [],
      source: 'dataforseo.labs.ranked_keywords',
      fetched_at: null,
      cache_hit: false,
    };
    return { ...mock, keywords: mock.keywords.slice(0, limit) };
  }
  const res = await api.get<{
    keywords?: OrganicKeywordDto[];
    source?: string;
    fetched_at?: string | null;
    cache_hit?: boolean;
  }>(`/seo/projects/${encodeURIComponent(projectId)}/organic-keywords`, { limit });
  return {
    keywords: (res.keywords ?? []).map((k) => ({
      keyword: k.keyword,
      position: numOrNull(k.position),
      volume: numOrNull(k.volume),
      traffic: numOrNull(k.traffic),
      url: strOrNull(k.url),
      difficulty: numOrNull(k.difficulty),
    })),
    source: res.source ?? 'dataforseo.labs.ranked_keywords',
    fetched_at: res.fetched_at ?? null,
    cache_hit: res.cache_hit === true,
  };
}

// ---- SEO: rank tracking (GET /seo/projects/{id}/rank-tracking) ---------------
// READ-ONLY from stored bc_seo_rankings (scheduler-fed). FREE — safe to load on
// mount; no provider call, no charge.
export interface RankTrackingResult {
  rankings: RankTrackingRow[];
  source: string;
}

interface RankTrackingRowDto {
  keyword: string;
  position?: number | string | null;
  previous_position?: number | string | null;
  delta?: number | string | null;
  checked_at?: string | null;
  source?: string;
}

const MOCK_RANK_TRACKING: Record<string, RankTrackingResult> = {
  seo1: {
    rankings: [
      { keyword: 'community growth platform', position: 4, previous_position: 6, delta: -2, checked_at: '2026-07-13T06:00:00Z', source: 'dataforseo_serp' },
      { keyword: 'peer support groups online', position: 11, previous_position: 11, delta: 0, checked_at: '2026-07-13T06:00:00Z', source: 'dataforseo_serp' },
      // null-honest: checked but not found within depth; no prior observation.
      { keyword: 'group coaching software uk', position: null, previous_position: null, delta: null, checked_at: '2026-07-12T06:00:00Z', source: 'dataforseo_serp' },
    ],
    source: 'bc_seo_rankings',
  },
  seo2: {
    rankings: [
      { keyword: 'booking software for therapists', position: 9, previous_position: 7, delta: 2, checked_at: '2026-07-13T06:05:00Z', source: 'dataforseo_serp' },
      { keyword: 'online booking system uk', position: 23, previous_position: 28, delta: -5, checked_at: '2026-07-13T06:05:00Z', source: 'dataforseo_serp' },
    ],
    source: 'bc_seo_rankings',
  },
};

/** Stored rank-tracking observations for one project (FREE, read-only). Safe on
 *  mount — reads bc_seo_rankings, never a provider call. */
export async function fetchSeoRankTracking(projectId: string): Promise<RankTrackingResult> {
  if (USE_MOCK_DATA) {
    return MOCK_RANK_TRACKING[projectId] ?? { rankings: [], source: 'bc_seo_rankings' };
  }
  const res = await api.get<{ rankings?: RankTrackingRowDto[]; source?: string }>(
    `/seo/projects/${encodeURIComponent(projectId)}/rank-tracking`,
  );
  return {
    rankings: (res.rankings ?? []).map((r) => ({
      keyword: r.keyword,
      position: numOrNull(r.position),
      previous_position: numOrNull(r.previous_position),
      delta: numOrNull(r.delta),
      checked_at: r.checked_at ?? null,
      source: r.source ?? 'bc_seo_rankings',
    })),
    source: res.source ?? 'bc_seo_rankings',
  };
}

// ---- SEO: data-quality benchmark (GET /seo/projects/{id}/benchmark) ----------
// DIRECTIONAL GSC-vs-provider check (roadmap §9.2). Reuses stored GSC rows + one
// guarded provider sample — user-triggered (surfaced in the Data quality strip).
interface BenchmarkComparisonDto {
  query: string;
  gsc_position?: number | string | null;
  provider_position?: number | string | null;
  delta?: number | string | null;
}

const MOCK_BENCHMARK: Record<string, BenchmarkResult> = {
  seo1: {
    benchmark: {
      sample_size: 3,
      gsc_vs_provider: [
        { query: 'community growth platform', gsc_position: 4.2, provider_position: 4, delta: -0.2 },
        { query: 'peer support groups online', gsc_position: 12.6, provider_position: 11, delta: -1.6 },
        // null-honest: GSC has this query, provider returned no position.
        { query: 'online community for coaches', gsc_position: 18.1, provider_position: null, delta: null },
      ],
      correlation_note: 'Broadly aligned — provider tracks GSC within ~2 positions on overlapping queries.',
    },
    source: 'gsc+dataforseo (directional, §9.2)',
    fetched_at: '2026-07-13T06:25:00Z',
  },
  seo2: {
    // null-honest: not enough overlapping queries to compare directionally.
    benchmark: null,
    source: 'gsc+dataforseo (directional, §9.2)',
    fetched_at: '2026-07-12T18:15:00Z',
  },
};

/**
 * Run the directional data-quality benchmark for one project — first-party GSC
 * ground truth vs one guarded DataForSEO sample over overlapping queries. Uses
 * a guarded provider sample, so it is USER-TRIGGERED. Same ApiError branches as
 * the other paid reads (503 not configured, 402 budget, 429, 502).
 */
export async function fetchSeoBenchmark(projectId: string): Promise<BenchmarkResult> {
  if (USE_MOCK_DATA) {
    return (
      MOCK_BENCHMARK[projectId] ?? {
        benchmark: null,
        source: 'gsc+dataforseo (directional, §9.2)',
        fetched_at: null,
      }
    );
  }
  const res = await api.get<{
    benchmark?: {
      sample_size?: number | string | null;
      gsc_vs_provider?: BenchmarkComparisonDto[];
      correlation_note?: string;
    } | null;
    source?: string;
    fetched_at?: string | null;
  }>(`/seo/projects/${encodeURIComponent(projectId)}/benchmark`);
  const b = res.benchmark ?? null;
  return {
    benchmark: b
      ? {
          sample_size: numOrNull(b.sample_size) ?? 0,
          gsc_vs_provider: (b.gsc_vs_provider ?? []).map((c) => ({
            query: c.query,
            gsc_position: numOrNull(c.gsc_position),
            provider_position: numOrNull(c.provider_position),
            delta: numOrNull(c.delta),
          })),
          correlation_note: b.correlation_note ?? '',
        }
      : null,
    source: res.source ?? 'gsc+dataforseo (directional, §9.2)',
    fetched_at: res.fetched_at ?? null,
  };
}

// ---- SEO: workspace spend snapshot (/seo/spend) -------------------------------
export interface SeoSpendSnapshot {
  /** "YYYY-MM" as reported by the API. */
  month: string;
  /** Pounds, converted from micro-pence (contract: 1 pence = 1,000,000 micros). */
  spent: number;
  /** Pounds, converted from budget_pence. */
  budget: number;
  currency: string;
}

/** Mutable so the mock PATCH (updateSeoBudget) is reflected by the next fetch.
 *  Matches the fetchSeoOverview mock so both SEO screens tell one story. */
const MOCK_SEO_SPEND: SeoSpendSnapshot = { month: '2026-07', spent: 35.2, budget: 50, currency: 'GBP' };

function adaptSeoSpend(s: SeoSpendDto): SeoSpendSnapshot {
  return {
    month: s.month,
    spent: s.spent_micro_pence / 1_000_000 / 100,
    budget: s.budget_pence / 100,
    currency: s.currency ?? 'GBP',
  };
}

export async function fetchSeoSpend(): Promise<SeoSpendSnapshot> {
  if (USE_MOCK_DATA) return { ...MOCK_SEO_SPEND };
  return adaptSeoSpend(await api.get<SeoSpendDto>('/seo/spend'));
}

/**
 * Change the workspace monthly budget cap (PATCH /v1/seo/budget, owner/operator).
 * Body: { budget_pence: integer, 0..1_000_000 } — callers validate and convert
 * pounds → pence before calling. Returns the SAME Spend snapshot shape as
 * GET /v1/seo/spend (200). 400 validation arrives as ApiError with the API
 * message for inline display.
 */
export async function updateSeoBudget(budgetPence: number): Promise<SeoSpendSnapshot> {
  if (USE_MOCK_DATA) {
    MOCK_SEO_SPEND.budget = budgetPence / 100;
    return { ...MOCK_SEO_SPEND };
  }
  return adaptSeoSpend(await api.patch<SeoSpendDto>('/seo/budget', { budget_pence: budgetPence }));
}

/* ============================================================================
 * Create flows — mutations wired for the "Add domain" / "Add lead" actions.
 * Denials read as human guidance (design-spec §9): the api client maps the
 * house error envelope to ApiError, and lead-ingest outcomes are turned into
 * friendly messages here. In USE_MOCK_DATA mode these no-op with a plausible
 * shape so the dev build still exercises the flow offline.
 * ========================================================================== */

// ---- SEO: create project (POST /v1/seo/projects) ----------------------------
export interface CreateSeoProjectInput {
  domain: string;
  display_name: string;
}

/**
 * Create an SEO project. Success 201 → { project }. Duplicate domain → 409 and
 * invalid input → 400 both arrive as ApiError (nested `{ error: { code } }`
 * envelope), so callers can branch on `.status` for tailored guidance.
 */
export async function createSeoProject(input: CreateSeoProjectInput): Promise<SeoProject> {
  if (USE_MOCK_DATA) {
    return adaptSeoProject(
      {
        id: Math.floor(Math.random() * 1_000_000),
        domain: input.domain,
        display_name: input.display_name,
        status: 'active',
        created_at: new Date().toISOString(),
      },
      0,
    );
  }
  const res = await api.post<{ project: SeoProjectDto }>('/seo/projects', input);
  return adaptSeoProject(res.project, 0); // brand-new project → no keywords yet
}

// ---- SEO: track keyword (POST /v1/seo/projects/{id}/keywords) ----------------
export interface CreateSeoKeywordInput {
  keyword: string;
  /** DataForSEO location id; API defaults to 2826 (United Kingdom) when omitted. */
  location_code?: number;
  /** API defaults to 'en' when omitted. */
  language?: string;
}

/**
 * Track a keyword on a project. 201 → { keyword }. Duplicate
 * (workspace, project, keyword, location) → 409 ApiError for the caller to
 * translate into friendly guidance. Idempotency header is added by the api
 * client on every mutation.
 */
export async function createSeoKeyword(
  projectId: string,
  input: CreateSeoKeywordInput,
): Promise<SeoKeywordRow> {
  if (USE_MOCK_DATA) {
    return adaptSeoKeyword(
      {
        id: Math.floor(Math.random() * 1_000_000),
        project_id: Number(projectId) || 0,
        keyword: input.keyword,
        location_code: input.location_code ?? 2826,
        language: input.language ?? 'en',
        created_at: new Date().toISOString(),
      },
      undefined, // brand-new keyword → no rank observation yet
    );
  }
  const body: Record<string, string | number> = { keyword: input.keyword };
  if (input.location_code !== undefined) body.location_code = input.location_code;
  if (input.language !== undefined) body.language = input.language;
  const res = await api.post<{ keyword: SeoKeywordDto }>(
    `/seo/projects/${encodeURIComponent(projectId)}/keywords`,
    body,
  );
  return adaptSeoKeyword(res.keyword, undefined);
}

// ---- SEO: run a rank check (POST /v1/seo/projects/{id}/rankings/check) -------
export interface SeoRankCheckResult {
  position: number | null;
  serp_url: string | null;
  source: string;
}

/**
 * Run ONE live rank check (paid — budget-guarded server-side). Callers must
 * branch on ApiError: 503 provider_unavailable = no provider wired yet
 * (nothing charged), 402 budget_exceeded = monthly cap would be exceeded
 * (no call made, nothing spent).
 */
export async function runSeoRankCheck(
  projectId: string,
  keywordId: string,
): Promise<SeoRankCheckResult> {
  if (USE_MOCK_DATA) {
    return { position: 7, serp_url: null, source: 'dataforseo_serp' };
  }
  const res = await api.post<{
    ranking_id: number | null;
    position: number | null;
    serp_url: string | null;
    source: string;
  }>(`/seo/projects/${encodeURIComponent(projectId)}/rankings/check`, {
    keyword_id: Number(keywordId),
  });
  return { position: res.position ?? null, serp_url: res.serp_url ?? null, source: res.source };
}

// ---- Sales: create lead (POST /v1/leads/ingest) -----------------------------
export interface CreateLeadInput {
  business_name?: string;
  business_email?: string;
  website?: string;
  city_region?: string;
  modality?: string;
  practitioner_name?: string;
}

/** One batch-ingest result row (LeadIngestService). */
interface IngestResultDto {
  outcome: 'stored' | 'duplicate' | 'suppressed' | 'error' | string;
  lead_ref?: string;
  errors?: string[];
}

/**
 * A non-"stored" ingest outcome — NOT a transport failure, so it is not an
 * ApiError. Carries a human-readable message the dialog renders inline.
 */
export class LeadCreateError extends Error {
  constructor(
    public readonly outcome: string,
    message: string,
  ) {
    super(message);
    this.name = 'LeadCreateError';
  }
}

function friendlyIngestOutcome(r: IngestResultDto): string {
  const detail = (r.errors ?? []).filter(Boolean).join(' ');
  switch (r.outcome) {
    case 'duplicate':
      return 'This business is already in your pipeline — the lead was not added again.';
    case 'suppressed':
      return (
        detail ||
        'This lead was suppressed — the business has opted out or is on the suppression list.'
      );
    case 'error':
      return detail || 'The lead could not be saved. Please check the details and try again.';
    default:
      return detail || `The lead was not stored (${r.outcome}).`;
  }
}

/**
 * Create a single lead via the batch ingest endpoint. `source_directory` is set
 * to "manual" automatically (not a user field); only user-provided fields are
 * sent. Returns the new lead_ref on "stored"; throws LeadCreateError with a
 * friendly message on duplicate / suppressed / error.
 */
export async function createLead(input: CreateLeadInput): Promise<{ lead_ref: string }> {
  if (USE_MOCK_DATA) {
    return { lead_ref: `lead_mock_${Math.random().toString(36).slice(2, 8)}` };
  }
  // Only include fields the user actually filled in (drop blanks so the API
  // does not reject an empty-string email etc.).
  const lead: Record<string, string> = { source_directory: 'manual' };
  for (const [k, v] of Object.entries(input)) {
    if (typeof v === 'string' && v.trim() !== '') lead[k] = v.trim();
  }
  const res = await api.post<{ results: IngestResultDto[] }>('/leads/ingest', { leads: [lead] });
  const result = res.results?.[0];
  if (!result) {
    throw new LeadCreateError('error', 'The lead could not be created — no result was returned.');
  }
  if (result.outcome === 'stored') {
    return { lead_ref: result.lead_ref ?? '' };
  }
  throw new LeadCreateError(result.outcome, friendlyIngestOutcome(result));
}

// ---- SEO scope contract (Phase-2 Lane 2C, honest attribution) ---------------
/** Scope self-description carried by the domain-aware SEO read surfaces
 *  (dashboard-summary / opportunities / alerts). `workspace` = figures span
 *  ALL connected domains; `domain` = fenced to ONE connected domain. Mirrors
 *  the backend additive `scope` field; the cockpit `/seo/summary` endpoint
 *  keeps a byte-exact shape, so its scope is derived from the request. */
export interface ScopeMeta {
  level: 'workspace' | 'domain';
  project_id?: number;
  hostname?: string;
}

// ---- Executive OS (/) --------------------------------------------------------
export interface ExecutiveOverview {
  /** Composite Business Health — null until a real aggregate exists (no API). */
  health: { score: number; evidence: Evidence } | null;
  /** AI executive summary — null in real mode (no summary endpoint yet). */
  summary: string | null;
  vitals: VitalSign[];
  attention: AttentionItem[];
  /** Workspace-wide ranked-organic + movement + alerts snapshot (honest states)
   *  for the dedicated Exec-OS SEO section. Null only when the SEO read failed. */
  seo: SeoExecSnapshot | null;
}

/** Swallow one sub-fetch failure so a partial Executive OS still renders,
 *  recording the error so a total failure can rethrow the real cause. */
async function settle<T>(p: Promise<T>, errors: unknown[]): Promise<T | null> {
  try {
    return await p;
  } catch (err) {
    errors.push(err);
    return null;
  }
}


export async function fetchExecutiveOverview(projectId?: number | null): Promise<ExecutiveOverview> {
  if (USE_MOCK_DATA) {
    return {
      health: { score: 78, evidence: MOCK_HEALTH_EVIDENCE },
      summary: MOCK_EXEC_SUMMARY,
      vitals: MOCK_VITALS,
      attention: MOCK_ATTENTION,
      seo: {
        totalOrganicKeywords: 50,
        pageOneKeywords: 8,
        strikingDistanceKeywords: 14,
        organicState: 'ok',
        gains: 4,
        losses: 3,
        movementState: 'workspace',
        openAlerts: 5,
        alertsState: 'ok',
        topActions: [
          { keyword: 'sound bath hackney', position: 13, target_page: 'https://omegahub.co.uk/sound-bath/', score: 71, action: 'Optimise the existing page for "sound bath hackney".' },
        ],
        trackedKeywords: 42,
        trackedProjects: 2,
        fetchedAt: '2026-07-17T06:30:00Z',
      },
    };
  }

  // Compose from what actually exists: /summary + /sending-status (Outreach),
  // /crm/opportunities (pipeline), /seo/spend. Each degrades independently.
  const failures: unknown[] = [];
  const [summary, sending, opps, spend, seoSignal] = await Promise.all([
    settle(api.get<SummaryDto>('/summary'), failures),
    settle(api.get<SendingStatusDto>('/sending-status'), failures),
    settle(api.get<PageDto<OpportunityDto>>('/crm/opportunities', { limit: 200 }), failures),
    settle(api.get<SeoSpendDto>('/seo/spend'), failures),
    settle(fetchSeoExecutiveSignal(projectId), failures),
  ]);

  const vitals: VitalSign[] = [];
  const attention: AttentionItem[] = [];

  if (summary) {
    const funnel = summary.funnel ?? {};
    const total = Object.values(funnel).reduce((s, n) => s + Number(n || 0), 0);
    const replies =
      Number(funnel.REPLIED_INTERESTED ?? 0) + Number(funnel.REPLIED_NOT_INTERESTED ?? 0);
    vitals.push(
      { key: 'leads', label: 'Leads discovered', value: String(total), engineKey: 'sales' },
      { key: 'replies', label: 'Replies', value: String(replies), engineKey: 'sales' },
    );
  }

  if (opps) {
    const open = (opps.items ?? []).filter((o) => o.status === 'open');
    const valuePence = open.reduce((s, o) => s + (o.value_pence ?? 0), 0);
    vitals.push({
      key: 'pipeline',
      label: 'Open pipeline',
      value: formatCurrency(valuePence / 100),
      engineKey: 'sales',
    });
    const fresh = open.filter((o) => o.stage === 'new').length;
    if (fresh > 0) {
      attention.push({
        id: 'opp_new',
        kind: 'opportunity',
        title: `${fresh} new ${fresh === 1 ? 'opportunity' : 'opportunities'} awaiting first touch`,
        detail: 'Opportunities in the New stage have no recorded next step yet.',
        engineKey: 'sales',
        href: '/sales/pipeline',
        severity: 'medium',
      });
    }
  }

  if (sending) {
    vitals.push({
      key: 'sending',
      label: 'Emails sent today',
      value: `${sending.sent_today} / ${sending.daily_cap}`,
      engineKey: 'sales',
    });
    if (sending.paused) {
      attention.push({
        id: 'send_paused',
        kind: 'risk',
        title: 'Outreach sending is paused',
        detail: sending.pause_reason
          ? `Reason: ${sending.pause_reason}. Resume is owner-only.`
          : 'The sending system paused itself. Resume is owner-only.',
        engineKey: 'sales',
        href: '/sales/leads',
        severity: 'high',
      });
    }
    const bounceWarn = sending.thresholds?.bounce_warn ?? 0.02;
    if (sending.bounce_rate > bounceWarn) {
      attention.push({
        id: 'bounce',
        kind: 'risk',
        title: 'Bounce rate above the warning threshold',
        detail: `Bounce rate ${(sending.bounce_rate * 100).toFixed(1)}% (warn at ${(bounceWarn * 100).toFixed(1)}%). Sending auto-pauses at the stop threshold.`,
        engineKey: 'sales',
        href: '/sales/leads',
        severity: 'medium',
      });
    }
    const complaintWarn = sending.thresholds?.complaint_warn ?? 0.0005;
    if (sending.complaint_rate > complaintWarn) {
      attention.push({
        id: 'complaint',
        kind: 'risk',
        title: 'Complaint rate above the warning threshold',
        detail: `Complaint rate ${(sending.complaint_rate * 100).toFixed(2)}%. Sending auto-pauses at the stop threshold.`,
        engineKey: 'sales',
        href: '/sales/leads',
        severity: 'high',
      });
    }
  }

  if (spend) {
    const budget = spend.budget_pence / 100;
    const spent = spend.spent_micro_pence / 1_000_000 / 100;
    vitals.push({
      key: 'seo_spend',
      label: 'SEO spend (month)',
      value: `${formatCurrency(spent)} / ${formatCurrency(budget)}`,
      engineKey: 'seo',
    });
    if (budget > 0 && spent / budget >= 0.85) {
      attention.push({
        id: 'seo_cap',
        kind: 'risk',
        title: 'SEO provider spend nearing the monthly cap',
        detail: `${formatCurrency(spent)} of ${formatCurrency(budget)} used. Provider calls pause at the cap.`,
        engineKey: 'seo',
        href: '/seo/projects',
        severity: 'medium',
      });
    }
  }

  if (seoSignal) {
    vitals.push(...seoSignal.vitals);
    attention.push(...seoSignal.attention);
  }

  // Gather the REAL signals once for the Business Health composite + AI summary.
  const funnel = summary?.funnel ?? {};
  const execLeads = summary ? Object.values(funnel).reduce((s, n) => s + Number(n || 0), 0) : null;
  const execReplies = summary
    ? Number(funnel.REPLIED_INTERESTED ?? 0) + Number(funnel.REPLIED_NOT_INTERESTED ?? 0)
    : null;
  const openOpps = opps ? (opps.items ?? []).filter((o) => o.status === 'open') : null;
  const openPipelinePence = openOpps ? openOpps.reduce((s, o) => s + (o.value_pence ?? 0), 0) : null;
  const freshOpps = openOpps ? openOpps.filter((o) => o.stage === 'new').length : null;
  const execSignals: ExecSignals = {
    leads: execLeads,
    replies: execReplies,
    openPipelinePence,
    freshOpps,
    sending,
    seo: seoSignal?.seo ?? null,
  };

  // Every sub-source failed → surface a real error state (rethrow the actual
  // cause so 403s render as human guidance) rather than an empty-but-fine
  // looking dashboard.
  if (!summary && !sending && !opps && !spend && !seoSignal) {
    throw failures[0] ?? new Error('No engine data source is reachable right now.');
  }

  // Business Health + AI Executive Summary are composed LIVE from the real
  // signals above (deterministic, source-attributed) — honest "calibrating"
  // only when NO signal has data. Live ranked-organic data => real content.
  const observedAt = new Date().toISOString();
  return {
    health: computeBusinessHealth(execSignals, observedAt),
    summary: composeExecSummary(execSignals),
    vitals,
    attention,
    seo: seoSignal?.seo ?? null,
  };
}

/* ----------------------------------------------------------------------------
 * SEO cockpit rollup (GET /v1/seo/summary) — Phase-2 Lane 2C scope-aware client.
 * Optional projectId scopes trackedKeywords + lastAuditAt to ONE connected
 * domain (projects stays the workspace domain-count); a foreign/other-tenant id
 * is rejected server-side (422) and surfaces as ApiError, never a silent
 * workspace-wide fall-back. The endpoint keeps a byte-exact cockpit shape, so
 * the honest `scope` is derived here from the request the caller made.
 * -------------------------------------------------------------------------- */
interface SeoSummaryDto {
  projects?: number | string | null;
  trackedKeywords?: number | string | null;
  lastAuditAt?: string | null;
}
export interface SeoSummary {
  projects: number;
  trackedKeywords: number;
  lastAuditAt: string | null;
  scope: ScopeMeta;
}
export async function fetchSeoSummary(projectId?: number): Promise<SeoSummary> {
  const scoped = typeof projectId === 'number' && projectId > 0;
  const n0 = (x: unknown): number => {
    const v = Math.trunc(Number(x ?? 0));
    return Number.isFinite(v) ? v : 0;
  };
  if (USE_MOCK_DATA) {
    return {
      projects: 4,
      trackedKeywords: scoped ? 12 : 48,
      lastAuditAt: '2026-07-18 02:10:00',
      scope: scoped ? { level: 'domain', project_id: projectId } : { level: 'workspace' },
    };
  }
  const res = scoped
    ? await api.get<SeoSummaryDto>('/seo/summary', { project_id: projectId })
    : await api.get<SeoSummaryDto>('/seo/summary');
  return {
    projects: n0(res.projects),
    trackedKeywords: n0(res.trackedKeywords),
    lastAuditAt: res.lastAuditAt ?? null,
    scope: scoped ? { level: 'domain', project_id: projectId } : { level: 'workspace' },
  };
}

/* ============================================================================
 * SEO Local Growth — Beta (LGB wave). Three user-triggered reads against the
 * LOCKED Local Growth contract (docs/brain/architecture/lgb/CONTRACTS.md):
 *   GET /seo/projects/{id}/local/discovery          — DataForSEO business_listings (paid, budget-guarded)
 *   GET /seo/projects/{id}/local/competitor-profile — DataForSEO gmb_info (paid, budget-guarded)
 *   GET /seo/projects/{id}/local/place-id           — Google Places ids-only (FREE, ≤3/day/tenant)
 * These fetchers are NEVER auto-fired: the page calls them only on an explicit
 * user action, so a mount never triggers a paid provider call. Every envelope
 * is source-attributed with fetched_at + cache_hit which the UI surfaces on the
 * card. Honesty rule: nullable provider fields stay null (never 0/invented);
 * absent creds surface as 503 provider_unavailable ("not configured"), 402
 * budget_exceeded / 429 rate_limited (place-id daily cap) / 502 provider_error
 * arrive as ApiError for the caller to render as human guidance.
 * ========================================================================== */

import type {
  LocalCompetitorProfile,
  LocalDiscoveryResult,
  LocalLocationOption,
  PlaceIdResolution,
} from '@/types';

/** Coerce a provider numeric (number | string | null | undefined) to a
 *  null-honest number: absent/blank/non-finite → null (never 0). */
function nullableNumber(v: unknown): number | null {
  if (v === null || v === undefined || v === '') return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

/** Coerce a provider scalar to a null-honest string; non-scalars (objects,
 *  arrays) → null so a raw provider shape is never rendered as a value. */
function nullableString(v: unknown): string | null {
  if (v === null || v === undefined) return null;
  if (typeof v === 'string') return v.trim() === '' ? null : v;
  if (typeof v === 'number' || typeof v === 'boolean') return String(v);
  return null;
}

/** "YYYY-MM-DD HH:MM:SS" — mirrors the contract's fetched_at shape (mock mode). */
function localGrowthStamp(): string {
  return new Date().toISOString().slice(0, 19).replace('T', ' ');
}

// ---- Local: discovery (GET .../local/discovery) ------------------------------
interface LocalDiscoveryResultDto {
  name?: string | null;
  address?: string | null;
  phone?: string | null;
  category?: string | null;
  rating?: number | string | null;
  review_count?: number | string | null;
  cid?: string | number | null;
}
interface LocalDiscoveryResponseDto {
  results?: LocalDiscoveryResultDto[];
  source?: string;
  fetched_at?: string;
  cache_hit?: boolean;
}

/** Discovery envelope adapted to null-honest UI rows + surfaced attribution. */
export interface LocalDiscoveryResponse {
  results: LocalDiscoveryResult[];
  source: string;
  fetched_at: string | null;
  cache_hit: boolean;
}

function adaptLocalDiscoveryResult(dto: LocalDiscoveryResultDto): LocalDiscoveryResult {
  return {
    name: nullableString(dto.name) ?? 'Unnamed listing',
    address: nullableString(dto.address),
    phone: nullableString(dto.phone),
    category: nullableString(dto.category),
    rating: nullableNumber(dto.rating),
    review_count: nullableNumber(dto.review_count),
    cid: nullableString(dto.cid),
  };
}

function mockLocalDiscovery(): LocalDiscoveryResponse {
  return {
    results: [
      { name: 'Cardiff Pilates Studio', address: '12 Queen St, Cardiff CF10 2BU', phone: '+44 29 2000 1234', category: 'Pilates studio', rating: 4.8, review_count: 214, cid: '10293847561029384756' },
      { name: 'Bay Reformer Pilates', address: '4 Mermaid Quay, Cardiff CF10 5BZ', phone: '+44 29 2011 5678', category: 'Fitness centre', rating: 4.6, review_count: 88, cid: '84756102938475610293' },
      // A deliberately sparse row — the provider returned only name + category;
      // the UI must render the rest as explicit em-dashes, never as zeros.
      { name: 'Movement Lab', address: null, phone: null, category: 'Pilates studio', rating: null, review_count: null, cid: null },
    ],
    source: 'dataforseo.business_listings',
    fetched_at: localGrowthStamp(),
    cache_hit: false,
  };
}

/**
 * Guarded local discovery (paid — budget-guarded + cached server-side). Call
 * ONLY on an explicit user action. Callers branch on ApiError: 503
 * provider_unavailable (not configured — nothing charged), 402 budget_exceeded
 * (nothing spent), 502 provider_error, 400 (missing query), 404 (unknown/foreign
 * project).
 */
export async function fetchLocalLocations(): Promise<LocalLocationOption[]> {
  if (USE_MOCK_DATA) {
    return [
      { key: 'uk', label: 'United Kingdom (no geo filter)' },
      { key: 'london', label: 'London' },
    ];
  }
  const res = await api.get<{ locations?: Array<{ key?: string; label?: string }> }>(
    '/seo/local/locations',
  );
  return (res.locations ?? [])
    .filter((l) => typeof l.key === 'string' && typeof l.label === 'string')
    .map((l) => ({ key: l.key as string, label: l.label as string }));
}

export async function fetchLocalDiscovery(
  projectId: string,
  input: { query: string; location?: string },
): Promise<LocalDiscoveryResponse> {
  if (USE_MOCK_DATA) return mockLocalDiscovery();
  const query: Record<string, string | number | undefined> = { query: input.query };
  if (input.location !== undefined) query.location = input.location;
  const res = await api.get<LocalDiscoveryResponseDto>(
    `/seo/projects/${encodeURIComponent(projectId)}/local/discovery`,
    query,
  );
  return {
    results: (res.results ?? []).map(adaptLocalDiscoveryResult),
    source: res.source ?? 'dataforseo.business_listings',
    fetched_at: res.fetched_at ?? null,
    cache_hit: res.cache_hit === true,
  };
}

// ---- Local: competitor profile (GET .../local/competitor-profile) ------------
interface LocalCompetitorProfileDto {
  name?: string | null;
  address?: string | null;
  phone?: string | null;
  category?: string | null;
  rating?: number | string | null;
  review_count?: number | string | null;
  website?: string | null;
  hours?: unknown;
  cid?: string | number | null;
}
interface LocalCompetitorProfileResponseDto {
  profile?: LocalCompetitorProfileDto | null;
  source?: string;
  fetched_at?: string;
  cache_hit?: boolean;
}

export interface LocalCompetitorProfileResponse {
  /** null = the provider returned no matching profile (honest empty). */
  profile: LocalCompetitorProfile | null;
  source: string;
  fetched_at: string | null;
  cache_hit: boolean;
}

function adaptLocalCompetitorProfile(dto: LocalCompetitorProfileDto): LocalCompetitorProfile {
  return {
    name: nullableString(dto.name) ?? 'Unnamed profile',
    address: nullableString(dto.address),
    phone: nullableString(dto.phone),
    category: nullableString(dto.category),
    rating: nullableNumber(dto.rating),
    review_count: nullableNumber(dto.review_count),
    website: nullableString(dto.website),
    hours: nullableString(dto.hours),
    cid: nullableString(dto.cid),
  };
}

function mockLocalCompetitorProfile(): LocalCompetitorProfileResponse {
  return {
    profile: {
      name: 'Cardiff Pilates Studio',
      address: '12 Queen St, Cardiff CF10 2BU',
      phone: '+44 29 2000 1234',
      category: 'Pilates studio',
      rating: 4.8,
      review_count: 214,
      website: 'https://cardiffpilates.example',
      hours: 'Mon–Fri 07:00–20:00 · Sat 08:00–14:00 · Sun closed',
      cid: '10293847561029384756',
    },
    source: 'dataforseo.gmb_info',
    fetched_at: localGrowthStamp(),
    cache_hit: true,
  };
}

/**
 * Guarded competitor Google Business Profile snapshot (paid — budget-guarded +
 * cached). Call ONLY on an explicit user action. Same ApiError branches as
 * discovery (503 not configured / 402 budget / 502 provider / 400 / 404).
 */
export async function fetchLocalCompetitorProfile(
  projectId: string,
  input: { keyword: string; location_code?: number },
): Promise<LocalCompetitorProfileResponse> {
  if (USE_MOCK_DATA) return mockLocalCompetitorProfile();
  const query: Record<string, string | number | undefined> = { keyword: input.keyword };
  if (input.location_code !== undefined) query.location_code = input.location_code;
  const res = await api.get<LocalCompetitorProfileResponseDto>(
    `/seo/projects/${encodeURIComponent(projectId)}/local/competitor-profile`,
    query,
  );
  return {
    profile: res.profile ? adaptLocalCompetitorProfile(res.profile) : null,
    source: res.source ?? 'dataforseo.gmb_info',
    fetched_at: res.fetched_at ?? null,
    cache_hit: res.cache_hit === true,
  };
}

// ---- Local: place-id resolution (GET .../local/place-id) — FREE SKU ----------
interface PlaceIdResponseDto {
  place_id?: string | null;
  source?: string;
  fetched_at?: string;
  cache_hit?: boolean;
}

/** Free Places id resolution envelope. place_id null = no match (honest). */
export interface PlaceIdResponse extends PlaceIdResolution {
  source: string;
  fetched_at: string | null;
  cache_hit: boolean;
}

function mockLocalPlaceId(): PlaceIdResponse {
  return {
    place_id: 'ChIJ-mock-Cardiff-Pilates-Studio',
    source: 'google.places.text_search_ids_only',
    fetched_at: localGrowthStamp(),
    cache_hit: false,
  };
}

/**
 * Resolve a Google Places place_id (FREE — no budget charge; cached
 * indefinitely; a hard ≤3 live resolutions/day/tenant cap is enforced
 * server-side). Call ONLY on an explicit user action. Callers branch on
 * ApiError: 429 rate_limited (daily cap reached), 503 provider_unavailable
 * (not configured), 502 provider_error, 400 (missing query), 404 (project).
 */
export async function fetchLocalPlaceId(
  projectId: string,
  input: { query: string },
): Promise<PlaceIdResponse> {
  if (USE_MOCK_DATA) return mockLocalPlaceId();
  const res = await api.get<PlaceIdResponseDto>(
    `/seo/projects/${encodeURIComponent(projectId)}/local/place-id`,
    { query: input.query },
  );
  return {
    place_id: res.place_id ?? null,
    source: res.source ?? 'google.places.text_search_ids_only',
    fetched_at: res.fetched_at ?? null,
    cache_hit: res.cache_hit === true,
  };
}
